<template>
  <Container
    :class="
      isNewsArticle
        ? 'px-container sm:px-container-lg 2xl:pl-80'
        : 'px-container sm:px-container-lg'
    "
  >
    <!-- CONTENT -->
    <AnimatedContainer
      v-for="(item, index) in data.content"
      :key="index"
      class="sm:text-typo-lg"
      :style="{ '--wysiwyg-text-color': '#6479AB' }"
    >
      <!-- Text -->
      <div
        v-if="item.type === 'text'"
        class="slide-up wysiwyg-custom-text wysiwyg-a-underline max-w-text"
        v-html="item.text"
      />

      <!-- Button -->
      <div v-if="item.type === 'button'" class="slide-up max-w-text">
        <Download v-if="item.download" class="my-4 w-fit" :file="item" />
        <AppLink
          v-if="!item.download && item.link"
          :href="item.link"
          :show-link-arrow="true"
          class="my-8 flex typo-button-sm md:typo-button-md"
          >{{ item.link_text }}</AppLink
        >
      </div>

      <!-- Text with Downloads -->
      <div
        v-if="item.type === 'text_with_downloads'"
        class="slide-up my-14 max-w-container gap-16 lg:flex 2xl:gap-24"
      >
        <div
          class="wysiwyg-custom-text wysiwyg-a-underline max-w-text"
          v-html="item.text_rich"
        />
        <div class="min-w-72 lg:w-1/4">
          <h3 class="mb-6 mt-8 typo-heading-sm sm:mb-14">
            {{ item.downloads.heading }}
          </h3>
          <ul>
            <li v-for="file in item.downloads.files" :key="file.id" class="">
              <Download class="mb-4 w-full max-w-210" :file="file" />
            </li>
            <li v-if="isTeamMember && item.vcard">
              <button
                type="button"
                target="_blank"
                class="group mb-4 flex w-full max-w-210 bg-beige-50 p-4 transition-transform duration-75 ease-in typo-button-sm hover:-translate-y-0.5 hover:duration-100 md:px-8 md:typo-button-md"
                @click="generateVCardFile"
              >
                <IconDownload class="mr-4 h-6" />
                <div>vCard</div>
              </button>
            </li>
          </ul>
        </div>
      </div>

      <!-- Text Two Columns -->
      <div
        v-if="item.type === 'text_column'"
        class="wysiwyg-custom-text wysiwyg-a-underline my-14 grid max-w-container gap-10 md:grid-cols-2 md:gap-16 lg:gap-24"
      >
        <div class="slide-up max-w-text" v-html="item.col_1_text_rich" />
        <div class="slide-up max-w-text" v-html="item.col_2_text_rich" />
      </div>
    </AnimatedContainer>
  </Container>
</template>

<script lang="ts" setup>
defineProps({
  data: { type: Object, required: true },
})

const entry = inject(EntryInjectionKey)!

const globals = inject(GlobalsKey)!
const isNewsArticle = entry.value?.blueprint === 'news_article'
const isTeamMember = entry.value?.blueprint === 'team_member'

function generateVCardFile() {
  if (!isTeamMember) return

  const general = globals.value.general as any
  const entryData = entry?.value as any

  const nameArray = entryData.title.split(' ')
  const familyName = nameArray?.pop() || ''
  const givenName = nameArray?.join(' ') || ''

  const addressText = `;;${general.street};${general.city};;${general.postal_code};${general.country}`

  const currentTimestamp = new Date().toISOString()

  imageToBase64(entryData.image.permalink, async (portraitBase64: string) => {
    // dont't add tabs as this will break the vCard-file
    const vCardContent = `BEGIN:VCARD
VERSION:3.0
FN;CHARSET=UTF-8:${givenName} ${familyName}
N;CHARSET=UTF-8:${familyName};${givenName};;;
EMAIL;CHARSET=UTF-8;type=WORK,INTERNET:${entryData.email}
TEL;TYPE=WORK,VOICE:${entryData.phone}
ADR;CHARSET=UTF-8;TYPE=WORK:${addressText}
ROLE;CHARSET=UTF-8:Partner
ORG;CHARSET=UTF-8:Baryon AG
URL;type=WORK;CHARSET=UTF-8:https://baryon.com
REV:${currentTimestamp}
PHOTO;TYPE=JPEG;ENCODING=BASE64:${portraitBase64.replace(
      'data:image/jpeg;base64,',
      ''
    )}
END:VCARD`

    const blob = new Blob([vCardContent], { type: 'text/vcard' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = `${entry?.value?.title.replace(' ', '-')}.vcf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  })
}

function imageToBase64(
  url: string,
  callback: (base64String: string | ArrayBuffer | null) => void
) {
  fetch(url)
    .then((response) => response.blob())
    .then((blob) => {
      const reader = new FileReader()
      reader.readAsDataURL(blob)
      reader.onloadend = () => {
        const base64String = reader.result

        callback(base64String)
      }
    })
}
</script>
